export const CT_STATUSES = [
    'planned',
    'assigned',
    'in_progress',
    'paused',
    'failed',
    'complete',
    'cancelled',
];

export const CT_PRIORITY_OPTIONS = ['Low', 'Medium', 'High'];

const CT_PRIORITY_RANK = {
    High: 0,
    Medium: 1,
    Low: 2,
};

/** Normalize EEI / legacy ICR priority labels to CT Low/Medium/High. */
export function normalizeCtPriority(priority) {
    if (priority == null || priority === '') return 'Medium';
    const p = String(priority).trim();
    if (/^high$/i.test(p)) return 'High';
    if (/^low$/i.test(p)) return 'Low';
    if (/^medium$/i.test(p)) return 'Medium';
    if (/^(critical|immediate)$/i.test(p)) return 'High';
    if (/^priority$/i.test(p)) return 'Medium';
    return 'Low';
}

/** Highest urgency among linked EEIs (High > Medium > Low). Defaults to Medium. */
export function highestLinkedEeiPriority(eeis = []) {
    if (!Array.isArray(eeis) || eeis.length === 0) return 'Medium';
    let best = 'Medium';
    let bestRank = CT_PRIORITY_RANK.Medium;
    for (const eei of eeis) {
        const normalized = normalizeCtPriority(eei?.priority);
        const rank = CT_PRIORITY_RANK[normalized] ?? CT_PRIORITY_RANK.Medium;
        if (rank < bestRank) {
            bestRank = rank;
            best = normalized;
        }
    }
    return best;
}

export const CT_CLASSIFICATION_OPTIONS = [
    'UNCLASSIFIED',
    'UNCLASSIFIED//FOUO',
    'CONFIDENTIAL',
    'SECRET',
    'SECRET//REL TO USA, FVEY',
    'TOP SECRET',
    'TOP SECRET//SCI',
];

/**
 * Maps a free-string CT classification (`CT_CLASSIFICATION_OPTIONS`, e.g.
 * "SECRET//REL TO USA, FVEY") onto the canonical U/C/S/TS scale used by
 * `ClassificationFieldsGroup` (Sources' catalog). Used to pre-fill a Collection
 * Material's classification from its parent Collection Task. Materials inherit the
 * task's Level/Distribution/Compartment at add time and those fields are read-only
 * in the Add Material dialogs — edit the task to change markings.
 */
export function normalizeCtClassificationToLevel(ctClassification) {
    const value = String(ctClassification || '').trim().toUpperCase();
    if (!value) return 'U';
    if (value.startsWith('TOP SECRET')) return 'TS';
    if (value.startsWith('SECRET')) return 'S';
    if (value.startsWith('CONFIDENTIAL')) return 'C';
    if (value.startsWith('UNCLASSIFIED')) return 'U';
    return 'U';
}

const LEVEL_TO_CT_CLASSIFICATION = {
    U: 'UNCLASSIFIED',
    C: 'CONFIDENTIAL',
    S: 'SECRET',
    TS: 'TOP SECRET',
};

/**
 * Reverse of `normalizeCtClassificationToLevel` — maps a canonical U/C/S/TS level onto the
 * base (non-caveat) `CT_CLASSIFICATION_OPTIONS` string stored in the Collection Task's legacy
 * `classification` field. The form no longer exposes a separate free-string classification
 * selector (it duplicated `ClassificationFieldsGroup`); instead `classification` is kept in sync
 * automatically from `classificationLevel` on every level change. Caveats (FOUO, REL TO, SCI)
 * are represented via the Distribution/Compartment fields instead of folded back into this
 * string.
 */
export function levelToCtClassification(level) {
    return LEVEL_TO_CT_CLASSIFICATION[String(level || '').trim().toUpperCase()] || 'UNCLASSIFIED';
}

export const CM_COLLECTION_METHOD_OPTIONS = [
    'IMINT',
    'SIGINT',
    'HUMINT',
    'OSINT',
    'GEOINT',
];

export const CM_ROLES = ['collection_manager', 'admin'];
export const COLLECTOR_ROLES = ['collector', 'admin'];
/** Who may add/remove tags on Collected Materials (product-owner decision, P9). */
export const CM_TAG_ROLES = ['collection_manager', 'admin', 'collector'];
/**
 * Who may add/remove tags on IIRs (P10). Tags are metadata, not content — like
 * CMs (P9 diagnosis), tag editing is role-based and independent of the IIR
 * draft/submitted/approved workflow status. Same population as CM_TAG_ROLES.
 */
export const IIR_TAG_ROLES = ['collection_manager', 'admin', 'collector'];
export const IIR_CREATE_ROLES = ['collector', 'collection_manager', 'admin'];
export const COLLECTION_TAB_ROLES = [
    'policymaker', 'collection_manager', 'collector', 'admin',
];

export function hasAnyRole(role, roles, allowed) {
    if (allowed.includes(role)) return true;
    return (roles || []).some((r) => allowed.includes(r));
}

/** Nav Collection section and ICR/EEI Collection tab. */
export function canAccessCollectionTab(role, roles) {
    return hasAnyRole(role, roles, COLLECTION_TAB_ROLES);
}

/**
 * URL key for CT routes — prefer the human-visible CT number over the UUID.
 * Accepts a task object or a raw id/number string.
 */
export function collectionTaskKey(taskOrNumber) {
    if (taskOrNumber == null || taskOrNumber === '') return null;
    if (typeof taskOrNumber === 'string') return taskOrNumber;
    return taskOrNumber.ctNumber || taskOrNumber.id || null;
}

/** `/collection/tasks/<ctNumber>` (falls back to UUID when number is missing). */
export function collectionTaskPath(taskOrNumber) {
    const key = collectionTaskKey(taskOrNumber);
    return key ? `/collection/tasks/${encodeURIComponent(key)}` : '/collection/tasks';
}

/** Relative returnTo URLs only (same contract as C2 cmViewReturn). */
export function sanitizeReturnTo(returnTo) {
    if (typeof returnTo !== 'string' || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
        return null;
    }
    return returnTo;
}

const CT_UI_STATUS_LABELS = {
    created: 'Created',
    assigned: 'Assigned',
    in_progress: 'In Progress',
    done: 'Done',
    cancelled: 'Cancelled',
    // Legacy / stored / derived API values
    planned: 'Created',
    unassigned: 'Created',
    started: 'In Progress',
    complete: 'Done',
};

/** User-facing CT workflow status derived from stored status + assignee/work products. */
export function ctUiStatusFromTask(task) {
    const stored = task?.status;
    if (stored === 'complete') return 'done';
    if (stored === 'cancelled') return 'cancelled';
    if (stored === 'in_progress' || task?.displayStatus === 'started'
        || (task?.materialsCount > 0) || (task?.iirsCount > 0)) {
        return 'in_progress';
    }
    if (task?.assigneeId) return 'assigned';
    return 'created';
}

/** Map UI workflow status to stored API status. */
export function ctStoredStatusForUi(uiStatus) {
    switch (uiStatus) {
        case 'created': return 'planned';
        case 'assigned': return 'assigned';
        case 'in_progress': return 'in_progress';
        case 'done': return 'complete';
        case 'cancelled': return 'cancelled';
        default: return uiStatus;
    }
}

/** Allowed manual UI status transitions from the current UI status. */
export function ctAllowedUiTransitions(uiStatus, {canManage = false, isAssignee = false, hasMaterials = false} = {}) {
    const canChange = canManage || isAssignee;
    switch (uiStatus) {
        case 'created':
            return canManage ? ['cancelled'] : [];
        case 'assigned':
            return canChange ? ['in_progress', 'cancelled'] : [];
        case 'in_progress': {
            const next = ['cancelled'];
            if (hasMaterials) next.unshift('done');
            return canChange ? next : [];
        }
        case 'done':
            return canManage ? ['in_progress', 'cancelled'] : [];
        case 'cancelled': {
            const reopen = ['in_progress'];
            if (hasMaterials) reopen.push('done');
            return canManage ? reopen : [];
        }
        default:
            return [];
    }
}

export function statusLabel(status) {
    if (!status) return '';
    const key = String(status).toLowerCase().replace(/-/g, '_');
    if (CT_UI_STATUS_LABELS[key]) return CT_UI_STATUS_LABELS[key];
    return String(status).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function statusColor(status) {
    switch (status) {
        case 'planned':
        case 'unassigned':
        case 'created':
            return 'default';
        case 'assigned':
            return 'info';
        case 'in_progress':
        case 'started':
            return 'primary';
        case 'paused':
            return 'warning';
        case 'failed':
            return 'error';
        case 'complete':
        case 'done':
            return 'success';
        case 'cancelled':
            return 'default';
        default:
            return 'default';
    }
}

/** MUI Chip color for a CT priority value (High / Medium / Low). */
export function priorityColor(priority) {
    switch (String(priority || '').toLowerCase()) {
        case 'high': return 'error';
        case 'medium': return 'warning';
        case 'low': return 'success';
        default: return 'default';
    }
}

export const PRODUCT_TYPE_COLLECTION_MATERIAL = {
    value: 'collection_material',
    label: 'Collected Material',
};

export const PRODUCT_TYPE_IIR_REPORT = {
    value: 'iir',
    label: 'IIR',
};
